"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  FilterFn,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchField?: string[];
  searchInputId?: string;
  noResults?: React.ReactNode;
  /**
   * Fade the body rows in on mount. Pass the result of useSawLoading so the
   * fade only runs when a loading skeleton actually preceded this table —
   * warm mounts should render instantly without re-fading.
   */
  animateBody?: boolean;
  /** Forwarded to Table's scroll wrapper — see Table's containerClassName. */
  containerClassName?: string;
  /** Merged onto TableHeader, e.g. to move its full-bleed top border. */
  headerClassName?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchField,
  searchInputId,
  noResults,
  animateBody = true,
  containerClassName,
  headerClassName,
}: DataTableProps<TData, TValue>) {
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    if (!searchInputId) return;

    const searchInput = document.getElementById(
      searchInputId,
    ) as HTMLInputElement;
    if (!searchInput) return;

    const handleSearchChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      setGlobalFilter(target.value);
    };

    searchInput.addEventListener("input", handleSearchChange);

    return () => {
      searchInput.removeEventListener("input", handleSearchChange);
    };
  }, [searchInputId]);

  const filterFn: FilterFn<TData> = (row, columnId, value) => {
    if (!value || value.length === 0) return true;

    const lowercaseValue = value.toLowerCase();

    const columnExists = (field: string) =>
      row.getAllCells().some((cell) => cell.column.id === field);

    if (Array.isArray(searchField)) {
      return searchField.some((field) => {
        if (!columnExists(field)) return false; // Skip if column doesn't exist
        const fieldValue = String(row.getValue(field) || "").toLowerCase();
        return fieldValue.includes(lowercaseValue);
      });
    } else if (searchField) {
      if (!columnExists(searchField)) return false; // Skip if column doesn't exist
      const fieldValue = String(row.getValue(searchField) || "").toLowerCase();
      return fieldValue.includes(lowercaseValue);
    }

    if (!columnExists(columnId)) return false; // Skip if column doesn't exist
    const cellValue = String(row.getValue(columnId) || "").toLowerCase();
    return cellValue.includes(lowercaseValue);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterFn,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  const hasExplicitSizes =
    columns.length > 0 && columns.every((c) => c.size != null);

  return (
    <div>
      <Table
        className={hasExplicitSizes ? "table-fixed" : undefined}
        containerClassName={containerClassName}
      >
        {hasExplicitSizes && (
          <colgroup>
            {columns.map((c, i) => (
              <col key={i} style={{ width: `${c.size}%` }} />
            ))}
          </colgroup>
        )}
        <TableHeader className={cn("bg-card", headerClassName)}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={`px-5 text-sm font-semibold ${
                      header.column.id === "direction" ? "text-center" : ""
                    }`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody
          className={
            animateBody ? "duration-300 animate-in fade-in" : undefined
          }
        >
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={`px-5 ${
                      cell.column.id === "direction" ? "text-center" : ""
                    }`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 p-0 text-center"
              >
                {noResults ?? "No results found"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
